import { useAnimals, useAnimal, useCreateAnimal, usePatchAnimalPedigree, type AnimalSummary } from '@/api/hooks';
import { AnimalAvatar } from '@/components/animals/AnimalAvatar';
import { Button } from '@/components/ui/Button';
import { DateInput } from '@/components/ui/DateInput';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { sexSymbol, formatDate } from '@/lib/format';
import {
  animalDisplayLabel,
  buildPedigreeSlots,
  calfCategoryCode,
  isInvalidPedigreePick,
  type PedigreeSlot,
  type PedigreeSlotKey,
} from '@/lib/pedigree';
import type { Animal } from '@/types/api';
import { GitBranch, Plus, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

type PickerState = {
  slot: PedigreeSlot;
};

export function AnimalPedigreePanel({ animalId }: { animalId: number }) {
  const { data: animal, isLoading: loadingAnimal } = useAnimal(animalId);
  const patchPedigree = usePatchAnimalPedigree();
  const createAnimal = useCreateAnimal();
  const [picker, setPicker] = useState<PickerState | null>(null);
  const [offspringOpen, setOffspringOpen] = useState(false);
  const [error, setError] = useState('');

  const propertyId = animal?.property_id;
  const { data: herd } = useAnimals({
    property_id: propertyId,
    per_page: 500,
    include_archive: true,
  });

  const animalsById = useMemo(() => {
    const map = new Map<number, Animal>();
    if (animal) map.set(animal.id, animal);
    for (const item of herd?.animals ?? []) {
      if (!map.has(item.id)) {
        map.set(item.id, {
          id: item.id,
          property_id: item.property_id,
          lot_id: item.lot_id,
          internal_number: item.internal_number,
          chip_number: null,
          registry_code: null,
          name: item.animal_name,
          sex: item.sex,
          kind: 'live',
          size: null,
          category_code: item.category_code,
          category_name: item.category_name,
          breed_id: null,
          breed_abbreviation: item.breed_abbreviation,
          breed_name: item.breed_name,
          birth_date: item.birth_date,
          birth_weight_kg: null,
          mother_id: item.mother_id ?? null,
          father_id: item.father_id ?? null,
          weaning_date: null,
          weaning_weight_kg: null,
          current_weight_kg: item.current_weight_kg,
          status: item.status,
          origin: null,
          notes: null,
          cover_photo_url: item.cover_photo_url,
        });
      }
    }
    return map;
  }, [animal, herd?.animals]);

  const slots = useMemo(
    () => (animal ? buildPedigreeSlots(animal, animalsById) : []),
    [animal, animalsById],
  );

  const offspring = useMemo(() => {
    if (!animal) return [];
    return (herd?.animals ?? [])
      .filter((item) => item.mother_id === animal.id || item.father_id === animal.id)
      .sort((a, b) => {
        const dateCmp = (b.birth_date ?? '').localeCompare(a.birth_date ?? '');
        return dateCmp !== 0 ? dateCmp : a.internal_number.localeCompare(b.internal_number, 'pt-BR');
      });
  }, [animal, herd?.animals]);

  const grandchildrenByParent = useMemo(() => {
    const map = new Map<number, typeof offspring>();
    for (const child of offspring) {
      const kids = (herd?.animals ?? []).filter(
        (item) => item.mother_id === child.id || item.father_id === child.id,
      );
      if (kids.length > 0) map.set(child.id, kids);
    }
    return map;
  }, [offspring, herd?.animals]);

  const slotMap = useMemo(
    () => Object.fromEntries(slots.map((slot) => [slot.key, slot])) as Record<PedigreeSlotKey, PedigreeSlot>,
    [slots],
  );

  async function applySlot(slot: PedigreeSlot, selectedId: number | null) {
    if (!animal) return;
    setError('');

    if (selectedId != null) {
      const invalid = isInvalidPedigreePick(slot.targetAnimalId, selectedId, animalsById);
      if (invalid) {
        setError(invalid);
        return;
      }
    }

    const refreshAnimalIds = [animal.id];
    if (animal.mother_id) refreshAnimalIds.push(animal.mother_id);
    if (animal.father_id) refreshAnimalIds.push(animal.father_id);
    if (slot.targetAnimalId > 0) refreshAnimalIds.push(slot.targetAnimalId);

    try {
      await patchPedigree.mutateAsync({
        animalId: slot.targetAnimalId,
        patch: { [slot.field]: selectedId },
        refreshAnimalIds,
      });
      setPicker(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível atualizar a afiliação.'));
    }
  }

  async function linkExistingOffspring(selectedId: number) {
    if (!animal) return;
    setError('');
    const invalid = isInvalidPedigreePick(selectedId, animal.id, animalsById);
    if (invalid) {
      setError(invalid);
      return;
    }
    const patch =
      animal.sex === 'female' ? { mother_id: animal.id } : { father_id: animal.id };
    try {
      await patchPedigree.mutateAsync({
        animalId: selectedId,
        patch,
        refreshAnimalIds: [animal.id, selectedId],
      });
      setOffspringOpen(false);
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível vincular a cria.'));
    }
  }

  async function createOffspring(payload: {
    internal_number: string;
    name: string;
    sex: 'male' | 'female';
    birth_date: string;
    other_parent_id: string;
  }) {
    if (!animal) return;
    setError('');
    try {
      await createAnimal.mutateAsync({
        property_id: animal.property_id,
        internal_number: payload.internal_number.trim(),
        name: payload.name.trim() || null,
        sex: payload.sex,
        category_code: calfCategoryCode(payload.sex),
        birth_date: payload.birth_date || null,
        mother_id: animal.sex === 'female' ? animal.id : payload.other_parent_id ? Number(payload.other_parent_id) : null,
        father_id: animal.sex === 'male' ? animal.id : payload.other_parent_id ? Number(payload.other_parent_id) : null,
        origin: 'birth',
      });
      setOffspringOpen(false);
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível cadastrar a cria.'));
    }
  }

  if (loadingAnimal || !animal) {
    return (
      <div className="h-48 rounded-2xl bg-manejai-sand/40 animate-pulse" />
    );
  }

  const motherAnimal = animal.mother_id ? animalsById.get(animal.mother_id) : null;
  const fatherAnimal = animal.father_id ? animalsById.get(animal.father_id) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-manejai-forest">Árvore genealógica</h2>
          <p className="text-sm text-manejai-bark/65">
            Escolha pais e avós, e registre as crias para seguir a linhagem adiante.
          </p>
        </div>
        <div className="inline-flex items-center gap-1.5 rounded-full bg-manejai-sage/20 px-3 py-1 text-xs font-medium text-manejai-forest">
          <GitBranch className="h-3.5 w-3.5" />
          Afiliação
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="overflow-x-auto pb-2">
        <div className="min-w-[760px] rounded-2xl border border-manejai-sand/80 bg-gradient-to-br from-manejai-cream/80 via-white to-manejai-sage/10 p-4 sm:p-6">
          <div className="grid grid-cols-[1.2fr_auto_1fr_auto_1fr] items-center gap-3 sm:gap-4">
            <PedigreeColumn title="Avós" subtitle="2 gerações acima dos pais">
              <div className="grid grid-cols-2 gap-2">
                <PedigreeNode
                  slot={slotMap.maternal_grandmother}
                  animal={slotMap.maternal_grandmother.value ? animalsById.get(slotMap.maternal_grandmother.value) : null}
                  tone="rose"
                  onPick={() => setPicker({ slot: slotMap.maternal_grandmother })}
                  onClear={() => applySlot(slotMap.maternal_grandmother, null)}
                  busy={patchPedigree.isPending}
                />
                <PedigreeNode
                  slot={slotMap.maternal_grandfather}
                  animal={slotMap.maternal_grandfather.value ? animalsById.get(slotMap.maternal_grandfather.value) : null}
                  tone="sky"
                  onPick={() => setPicker({ slot: slotMap.maternal_grandfather })}
                  onClear={() => applySlot(slotMap.maternal_grandfather, null)}
                  busy={patchPedigree.isPending}
                />
                <PedigreeNode
                  slot={slotMap.paternal_grandmother}
                  animal={slotMap.paternal_grandmother.value ? animalsById.get(slotMap.paternal_grandmother.value) : null}
                  tone="rose"
                  onPick={() => setPicker({ slot: slotMap.paternal_grandmother })}
                  onClear={() => applySlot(slotMap.paternal_grandmother, null)}
                  busy={patchPedigree.isPending}
                />
                <PedigreeNode
                  slot={slotMap.paternal_grandfather}
                  animal={slotMap.paternal_grandfather.value ? animalsById.get(slotMap.paternal_grandfather.value) : null}
                  tone="sky"
                  onPick={() => setPicker({ slot: slotMap.paternal_grandfather })}
                  onClear={() => applySlot(slotMap.paternal_grandfather, null)}
                  busy={patchPedigree.isPending}
                />
              </div>
            </PedigreeColumn>

            <PedigreeConnector />

            <PedigreeColumn title="Pais" subtitle="Mãe e pai diretos">
              <div className="flex flex-col gap-3">
                <PedigreeNode
                  slot={slotMap.mother}
                  animal={motherAnimal}
                  tone="rose"
                  onPick={() => setPicker({ slot: slotMap.mother })}
                  onClear={() => applySlot(slotMap.mother, null)}
                  busy={patchPedigree.isPending}
                />
                <PedigreeNode
                  slot={slotMap.father}
                  animal={fatherAnimal}
                  tone="sky"
                  onPick={() => setPicker({ slot: slotMap.father })}
                  onClear={() => applySlot(slotMap.father, null)}
                  busy={patchPedigree.isPending}
                />
              </div>
            </PedigreeColumn>

            <PedigreeConnector />

            <PedigreeColumn title="Este animal" subtitle="Foco da árvore">
              <div className="flex justify-center">
                <div className="w-full max-w-[11rem] rounded-2xl border-2 border-manejai-forest bg-white p-3 shadow-sm ring-4 ring-manejai-sage/25">
                  <div className="flex flex-col items-center text-center">
                    <AnimalAvatar
                      coverPhotoUrl={animal.cover_photo_url}
                      internalNumber={animal.internal_number}
                      sex={animal.sex}
                      size="lg"
                    />
                    <p className="mt-2 text-sm font-semibold text-manejai-forest">{animal.internal_number}</p>
                    {animal.name && (
                      <p className="text-xs text-manejai-bark/70">{animal.name}</p>
                    )}
                    {animal.birth_date && (
                      <p className="text-[11px] text-manejai-bark/55">Nasc. {formatDate(animal.birth_date)}</p>
                    )}
                    <span className="mt-1 rounded-full bg-manejai-forest/10 px-2 py-0.5 text-[10px] font-medium text-manejai-forest">
                      {sexSymbol(animal.sex)} {animal.category_name}
                    </span>
                  </div>
                </div>
              </div>
            </PedigreeColumn>
          </div>
        </div>
      </div>

      <p className="text-xs text-manejai-bark/55">
        Avós são preenchidos nos registros da mãe e do pai. As crias aparecem abaixo e podem ter a própria linhagem na ficha de cada uma.
      </p>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-manejai-forest">Crias</h3>
            <p className="text-xs text-manejai-bark/55">
              Filhos(as) deste animal — escolha um cadastro existente ou inclua um novo.
            </p>
          </div>
          <Button type="button" size="sm" onClick={() => setOffspringOpen(true)}>
            <Plus className="h-4 w-4" />
            Incluir cria
          </Button>
        </div>

        {offspring.length === 0 ? (
          <button
            type="button"
            onClick={() => setOffspringOpen(true)}
            className="flex min-h-[5.5rem] w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed border-manejai-sand bg-white/70 px-4 py-5 text-center transition hover:border-manejai-moss hover:bg-manejai-cream/40"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-manejai-sage/20 text-manejai-forest">
              <Plus className="h-4 w-4" />
            </span>
            <span className="mt-2 text-sm font-medium text-manejai-forest">Adicionar primeira cria</span>
            <span className="text-[11px] text-manejai-bark/55">Escolher animal ou cadastrar na hora</span>
          </button>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {offspring.map((child) => {
              const grandkids = grandchildrenByParent.get(child.id) ?? [];
              return (
                <div
                  key={child.id}
                  className="rounded-2xl border border-manejai-sand/80 bg-white p-3 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <AnimalAvatar
                      coverPhotoUrl={child.cover_photo_url}
                      internalNumber={child.internal_number}
                      sex={child.sex}
                      size="sm"
                    />
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/animais/${child.id}`}
                        className="block truncate text-sm font-semibold text-manejai-forest hover:underline"
                      >
                        {child.internal_number}
                      </Link>
                      {child.animal_name && (
                        <p className="truncate text-xs text-manejai-bark/65">{child.animal_name}</p>
                      )}
                      <p className="text-[11px] text-manejai-bark/50">
                        {sexSymbol(child.sex)} {child.category_name}
                        {child.birth_date ? ` · Nasc. ${formatDate(child.birth_date)}` : ''}
                      </p>
                    </div>
                  </div>
                  {grandkids.length > 0 && (
                    <div className="mt-2 space-y-1 border-t border-manejai-sand/60 pt-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-manejai-bark/45">
                        Linhagem
                      </p>
                      {grandkids.map((kid) => (
                        <Link
                          key={kid.id}
                          to={`/animais/${kid.id}`}
                          className="flex items-center justify-between rounded-lg px-1.5 py-1 text-xs text-manejai-forest hover:bg-manejai-cream/70"
                        >
                          <span className="truncate">
                            {kid.internal_number}
                            {kid.animal_name ? ` — ${kid.animal_name}` : ''}
                          </span>
                          <span className="text-manejai-bark/45">{sexSymbol(kid.sex)}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {picker && (
        <AnimalPedigreePickerModal
          open
          onClose={() => setPicker(null)}
          propertyId={animal.property_id}
          slot={picker.slot}
          animalsById={animalsById}
          onSelect={(id) => applySlot(picker.slot, id)}
          loading={patchPedigree.isPending}
        />
      )}

      {offspringOpen && (
        <OffspringEditorModal
          open
          onClose={() => setOffspringOpen(false)}
          animal={animal}
          herd={herd?.animals ?? []}
          animalsById={animalsById}
          loading={patchPedigree.isPending || createAnimal.isPending}
          onPickExisting={linkExistingOffspring}
          onCreate={createOffspring}
        />
      )}
    </div>
  );
}

function PedigreeColumn({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-manejai-moss">{title}</p>
      <p className="mb-2 text-[11px] text-manejai-bark/55">{subtitle}</p>
      {children}
    </div>
  );
}

function PedigreeConnector() {
  return (
    <div className="hidden h-full min-h-[8rem] items-center justify-center sm:flex">
      <div className="flex flex-col items-center gap-2 text-manejai-moss/50">
        <span className="h-px w-8 bg-manejai-moss/30" />
        <GitBranch className="h-4 w-4 rotate-90" />
        <span className="h-px w-8 bg-manejai-moss/30" />
      </div>
    </div>
  );
}

function PedigreeNode({
  slot,
  animal,
  tone,
  onPick,
  onClear,
  busy,
}: {
  slot: PedigreeSlot;
  animal?: Animal | null;
  tone: 'rose' | 'sky';
  onPick: () => void;
  onClear: () => void;
  busy: boolean;
}) {
  const toneClasses =
    tone === 'rose'
      ? 'border-rose-200/80 bg-rose-50/50 hover:border-rose-300'
      : 'border-sky-200/80 bg-sky-50/50 hover:border-sky-300';

  if (slot.disabled) {
    return (
      <div className="rounded-xl border border-dashed border-manejai-sand bg-manejai-sand/20 p-3 text-center">
        <p className="text-xs font-medium text-manejai-bark/45">{slot.shortLabel}</p>
        <p className="mt-1 text-[10px] text-manejai-bark/40">{slot.disabledReason}</p>
      </div>
    );
  }

  if (!animal) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={onPick}
        className={cn(
          'group flex min-h-[7.5rem] w-full flex-col items-center justify-center rounded-xl border-2 border-dashed p-3 text-center transition',
          toneClasses,
          'disabled:opacity-50',
        )}
      >
        <span className="text-[11px] font-semibold text-manejai-bark/70">{slot.label}</span>
        <span className="mt-2 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-manejai-forest shadow-sm transition group-hover:scale-105">
          <Plus className="h-4 w-4" />
        </span>
        <span className="mt-2 text-[10px] text-manejai-bark/55">Escolher animal</span>
      </button>
    );
  }

  return (
    <div className={cn('relative rounded-xl border p-2.5 shadow-sm', toneClasses)}>
      <div className="flex items-start gap-2">
        <AnimalAvatar
          coverPhotoUrl={animal.cover_photo_url}
          internalNumber={animal.internal_number}
          sex={animal.sex}
          size="sm"
        />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-manejai-bark/50">{slot.label}</p>
          <Link to={`/animais/${animal.id}`} className="block truncate text-sm font-semibold text-manejai-forest hover:underline">
            {animal.internal_number}
          </Link>
                    {animal.name && <p className="truncate text-[11px] text-manejai-bark/65">{animal.name}</p>}
                    {animal.birth_date && (
                      <p className="mt-0.5 text-[10px] text-manejai-bark/50">Nasc. {formatDate(animal.birth_date)}</p>
                    )}
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={onClear}
          className="rounded-md p-1 text-manejai-bark/40 hover:bg-white/70 hover:text-red-600 disabled:opacity-50"
          aria-label={`Remover ${slot.label}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={onPick}
        className="mt-2 w-full rounded-lg bg-white/70 px-2 py-1 text-[10px] font-medium text-manejai-forest hover:bg-white disabled:opacity-50"
      >
        Trocar
      </button>
    </div>
  );
}

function AnimalPedigreePickerModal({
  open,
  onClose,
  propertyId,
  slot,
  animalsById,
  onSelect,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  propertyId: number;
  slot: PedigreeSlot;
  animalsById: Map<number, Animal>;
  onSelect: (animalId: number) => void;
  loading: boolean;
}) {
  const { data: herd } = useAnimals({ property_id: propertyId, per_page: 500, include_archive: true });
  const [search, setSearch] = useState('');
  const [localError, setLocalError] = useState('');

  const options = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (herd?.animals ?? [])
      .filter((a) => a.sex === slot.requiredSex)
      .filter((a) => {
        if (!q) return true;
        return (
          a.internal_number.toLowerCase().includes(q) ||
          (a.animal_name?.toLowerCase().includes(q) ?? false)
        );
      })
      .sort((a, b) => a.internal_number.localeCompare(b.internal_number, 'pt-BR'));
  }, [herd?.animals, search, slot.requiredSex]);

  function handlePick(id: number) {
    const invalid = isInvalidPedigreePick(slot.targetAnimalId, id, animalsById);
    if (invalid) {
      setLocalError(invalid);
      return;
    }
    setLocalError('');
    onSelect(id);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Escolher ${slot.label.toLowerCase()}`}
      size="md"
      footer={
        <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-manejai-bark/65">
          Selecione um animal {slot.requiredSex === 'female' ? 'fêmea' : 'macho'} cadastrado nesta fazenda.
        </p>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-manejai-bark/40" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por código ou nome"
            className="pl-9"
          />
        </div>

        {localError && <p className="text-sm text-red-600">{localError}</p>}

        <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
          {options.length === 0 ? (
            <p className="py-6 text-center text-sm text-manejai-bark/55">
              Nenhum animal encontrado para este papel.
            </p>
          ) : (
            options.map((item) => (
              <button
                key={item.id}
                type="button"
                disabled={loading}
                onClick={() => handlePick(item.id)}
                className="flex w-full items-center gap-3 rounded-xl border border-manejai-sand/80 bg-white px-3 py-2.5 text-left transition hover:border-manejai-moss hover:bg-manejai-cream/40 disabled:opacity-50"
              >
                <AnimalAvatar
                  coverPhotoUrl={item.cover_photo_url}
                  internalNumber={item.internal_number}
                  sex={item.sex}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-manejai-forest">
                    {animalDisplayLabel({
                      internal_number: item.internal_number,
                      name: item.animal_name,
                    })}
                  </p>
                  <p className="text-xs text-manejai-bark/55">{item.category_name}</p>
                  {item.birth_date && (
                    <p className="text-[11px] text-manejai-bark/45">Nasc. {formatDate(item.birth_date)}</p>
                  )}
                </div>
                <span className="text-sm text-manejai-bark/45">{sexSymbol(item.sex)}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

function OffspringEditorModal({
  open,
  onClose,
  animal,
  herd,
  animalsById,
  loading,
  onPickExisting,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  animal: Animal;
  herd: AnimalSummary[];
  animalsById: Map<number, Animal>;
  loading: boolean;
  onPickExisting: (id: number) => void;
  onCreate: (payload: {
    internal_number: string;
    name: string;
    sex: 'male' | 'female';
    birth_date: string;
    other_parent_id: string;
  }) => void;
}) {
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [search, setSearch] = useState('');
  const [localError, setLocalError] = useState('');
  const [sex, setSex] = useState<'male' | 'female'>('female');
  const [internalNumber, setInternalNumber] = useState('');
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [otherParentId, setOtherParentId] = useState('');

  const otherParentLabel = animal.sex === 'female' ? 'Touro (pai)' : 'Mãe';
  const otherParentSex = animal.sex === 'female' ? 'male' : 'female';

  const existingOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return herd
      .filter((item) => item.id !== animal.id)
      .filter((item) => item.mother_id !== animal.id && item.father_id !== animal.id)
      .filter((item) => {
        if (!q) return true;
        return (
          item.internal_number.toLowerCase().includes(q) ||
          (item.animal_name?.toLowerCase().includes(q) ?? false)
        );
      })
      .sort((a, b) => a.internal_number.localeCompare(b.internal_number, 'pt-BR'));
  }, [herd, animal.id, search]);

  const otherParentOptions = useMemo(
    () =>
      herd
        .filter((item) => item.id !== animal.id && item.sex === otherParentSex)
        .map((item) => ({
          value: item.id,
          label: `${item.internal_number}${item.animal_name ? ` — ${item.animal_name}` : ''}`,
        })),
    [herd, animal.id, otherParentSex],
  );

  function handlePick(id: number) {
    const invalid = isInvalidPedigreePick(id, animal.id, animalsById);
    if (invalid) {
      setLocalError(invalid);
      return;
    }
    setLocalError('');
    onPickExisting(id);
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!internalNumber.trim()) {
      setLocalError('Informe o código da cria.');
      return;
    }
    setLocalError('');
    onCreate({
      internal_number: internalNumber,
      name,
      sex,
      birth_date: birthDate,
      other_parent_id: otherParentId,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Incluir cria"
      size="md"
      footer={
        mode === 'new' ? (
          <>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" form="offspring-create-form" loading={loading}>
              Cadastrar e vincular
            </Button>
          </>
        ) : (
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
        )
      }
    >
      <div className="space-y-3">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMode('existing')}
            className={cn(
              'flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition',
              mode === 'existing'
                ? 'border-manejai-forest bg-manejai-forest/5 text-manejai-forest'
                : 'border-manejai-sand text-manejai-bark/70 hover:bg-manejai-cream/40',
            )}
          >
            Escolher animal
          </button>
          <button
            type="button"
            onClick={() => setMode('new')}
            className={cn(
              'flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition',
              mode === 'new'
                ? 'border-manejai-forest bg-manejai-forest/5 text-manejai-forest'
                : 'border-manejai-sand text-manejai-bark/70 hover:bg-manejai-cream/40',
            )}
          >
            Cadastrar agora
          </button>
        </div>

        {localError && <p className="text-sm text-red-600">{localError}</p>}

        {mode === 'existing' ? (
          <>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-manejai-bark/40" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por código ou nome"
                className="pl-9"
              />
            </div>
            <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
              {existingOptions.length === 0 ? (
                <p className="py-6 text-center text-sm text-manejai-bark/55">
                  Nenhum animal disponível. Cadastre uma cria nova.
                </p>
              ) : (
                existingOptions.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    disabled={loading}
                    onClick={() => handlePick(item.id)}
                    className="flex w-full items-center gap-3 rounded-xl border border-manejai-sand/80 bg-white px-3 py-2.5 text-left transition hover:border-manejai-moss hover:bg-manejai-cream/40 disabled:opacity-50"
                  >
                    <AnimalAvatar
                      coverPhotoUrl={item.cover_photo_url}
                      internalNumber={item.internal_number}
                      sex={item.sex}
                      size="sm"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-manejai-forest">
                        {animalDisplayLabel({
                          internal_number: item.internal_number,
                          name: item.animal_name,
                        })}
                      </p>
                      <p className="text-xs text-manejai-bark/55">{item.category_name}</p>
                    </div>
                    <span className="text-sm text-manejai-bark/45">{sexSymbol(item.sex)}</span>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <form id="offspring-create-form" onSubmit={handleCreate} className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Código"
              required
              value={internalNumber}
              onChange={(e) => setInternalNumber(e.target.value)}
            />
            <Input
              label="Nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Opcional"
            />
            <Select
              label="Sexo"
              value={sex}
              onChange={(e) => setSex(e.target.value as 'male' | 'female')}
              options={[
                { value: 'female', label: 'Fêmea' },
                { value: 'male', label: 'Macho' },
              ]}
            />
            <DateInput label="Nascimento" value={birthDate} onChange={setBirthDate} />
            <div className="sm:col-span-2">
              <Select
                label={otherParentLabel}
                value={otherParentId}
                onChange={(e) => setOtherParentId(e.target.value)}
                placeholder="Opcional"
                allowEmpty
                options={otherParentOptions}
              />
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
